<template>
  <v-dialog
    :value="showDialog"
    persistent
    max-width="1250"
  >
    <v-card>
      <v-overlay
        :value="busy"
        absolute
      >
        <v-progress-circular indeterminate />
      </v-overlay>
      <v-card-title>Correct reading history — {{ accountNumber }}</v-card-title>
      <v-card-text>
        <v-alert
          v-if="error"
          type="error"
          outlined
        >
          {{ error }}
        </v-alert>
        <v-progress-linear
          v-if="loading"
          indeterminate
        />
        <v-tabs v-model="tab">
          <v-tab>Correction</v-tab>
          <v-tab>History</v-tab>
        </v-tabs>
        <div v-if="tab === 0 && !loading">
          <template v-if="!preview">
            <p class="mt-4">
              Tick the readings you want to correct, then update their values below. Any later readings affected by a changed meter value will be linked automatically and shown in the preview.
            </p>
            <v-sheet
              outlined
              rounded
              class="d-flex align-center flex-wrap pa-3 mb-4"
            >
              <div>
                <div class="subtitle-2">
                  Choose readings to correct
                </div>
                <div class="caption">
                  Selected rows become editable. Load older months only when you need an older reading.
                </div>
              </div>
              <v-spacer />
              <v-chip
                class="ma-1"
                color="primary"
                outlined
                small
              >
                {{ selected.length }} selected
              </v-chip>
              <v-btn
                color="primary"
                text
                small
                @click="selectLatestMonths"
              >
                Select latest 2
              </v-btn>
              <v-btn
                text
                small
                :disabled="!selected.length"
                @click="clearSelection"
              >
                Clear
              </v-btn>
            </v-sheet>
            <v-alert
              type="info"
              text
            >
              For older bills without saved tariffs, enter the historical unit rate and revised standing charge for every affected month, including the following month if its opening reading changes.
            </v-alert>
            <v-simple-table class="correction-grid">
              <thead><tr><th>Correct?</th><th>Month / meter</th><th>Reading type</th><th>Previous</th><th>Current</th><th>Billed units</th><th>Historical rate / charge</th><th>Existing bill</th></tr></thead>
              <tbody>
                <tr
                  v-for="row in displayRows"
                  :key="row.id"
                  :class="{ 'grey--text': !row.selected }"
                >
                  <td>
                    <v-checkbox
                      v-model="row.selected"
                      :aria-label="`Correct ${row.month}`"
                      hide-details
                    />
                  </td>
                  <td class="month-cell">
                    {{ row.month }}<small class="d-block">{{ row.meter_number }}</small>
                  </td>
                  <td>
                    <v-select
                      v-model="row.mode"
                      :disabled="!row.selected"
                      :items="modes"
                      label="Type"
                      dense
                      hide-details
                    />
                    <v-checkbox
                      v-model="row.reading_baseline"
                      :disabled="!row.selected"
                      dense
                      hide-details
                    >
                      <template v-slot:label>
                        <span>Manual opening reading</span>
                        <span @click.stop>
                          <v-tooltip
                            top
                            max-width="320"
                          >
                            <template v-slot:activator="{ on, attrs }">
                              <v-icon
                                small
                                class="ml-1"
                                aria-label="About manual opening readings"
                                v-bind="attrs"
                                v-on="on"
                              >
                                {{ icons.mdiHelpCircleOutline }}
                              </v-icon>
                            </template>
                            Use this only after a meter installation, replacement, or reset. It lets you enter the previous reading manually instead of linking it from the preceding month.
                          </v-tooltip>
                        </span>
                      </template>
                    </v-checkbox>
                  </td>
                  <td>
                    <v-text-field
                      :value="linkedPrevious(row)"
                      :disabled="!row.selected || !row.reading_baseline"
                      type="number"
                      min="0"
                      step="1"
                      label="Previous"
                      dense
                      hide-details
                      @input="row.previous_reading = $event"
                    />
                  </td>
                  <td>
                    <v-text-field
                      v-model="row.current_reading"
                      :disabled="!row.selected"
                      type="number"
                      min="0"
                      step="1"
                      label="Current"
                      dense
                      hide-details
                    />
                  </td>
                  <td>
                    <v-text-field
                      v-if="row.mode === 'estimated'"
                      v-model="row.adjusted_units"
                      :disabled="!row.selected"
                      type="number"
                      min="0"
                      step="0.001"
                      label="Estimated units"
                      dense
                      hide-details
                    />
                    <span v-else>{{ row.current_reading - linkedPrevious(row) }}</span>
                    <v-text-field
                      v-if="row.mode === 'estimated'"
                      v-model="row.estimation_basis"
                      :disabled="!row.selected"
                      label="Estimation basis"
                      dense
                    />
                  </td>
                  <td class="historical-charge-cell">
                    <small
                      v-if="row.tariff_snapshot"
                      class="d-block mb-2"
                    >Saved tariff used unless you edit these charges</small>
                    <v-text-field
                      v-model="row.unit_rate"
                      class="historical-charge-field"
                      :disabled="!row.selected"
                      type="number"
                      min="0"
                      label="Unit rate (KSh)"
                      dense
                      hide-details
                    />
                    <v-text-field
                      v-model="row.service_fee"
                      class="historical-charge-field"
                      :disabled="!row.selected"
                      type="number"
                      min="0"
                      label="Standing charge (KSh)"
                      dense
                      hide-details
                    />
                  </td>
                  <td>{{ money(row.bill) }}</td>
                </tr>
              </tbody>
            </v-simple-table>
            <div
              v-if="rows.length"
              class="d-flex align-center flex-wrap pt-3"
            >
              <span class="caption">
                Showing the latest {{ Math.min(visibleMonthCount, allMonths.length) }} of {{ allMonths.length }} months<span v-if="hasSelectedOutsideWindow">, plus selected older months</span>.
              </span>
              <v-spacer />
              <v-btn
                v-if="remainingMonthCount"
                color="primary"
                text
                small
                @click="loadMoreMonths"
              >
                Load older months ({{ remainingMonthCount }} remaining)
              </v-btn>
            </div>
            <p
              v-if="rows.length === 0"
              class="mt-4"
            >
              No readings are available for this customer.
            </p>
            <v-textarea
              v-model="reason"
              class="mt-4"
              label="Reason for correction (required)"
              rows="2"
              counter="2000"
            />
            <v-checkbox
              v-model="sendSummary"
              label="Send one correction-summary SMS after saving"
            />
          </template>
          <template v-else>
            <v-alert
              type="info"
              text
              class="mt-4"
            >
              Review all affected months below. Saving applies the entire correction together.
            </v-alert>
            <p>Bill change: <strong>KSh {{ money(preview.bill_change) }}</strong></p>
            <p>Account balance: <strong>{{ balanceLabel(preview.balance_before) }} → {{ balanceLabel(preview.balance_after) }}</strong></p>
            <p>Reason: {{ reason }}</p>
            <correction-changes :rows="preview.rows" />
          </template>
        </div>
        <div
          v-if="tab === 1"
          class="mt-4"
        >
          <p v-if="history.length === 0">
            No batch corrections have been recorded.
          </p>
          <v-expansion-panels>
            <v-expansion-panel
              v-for="entry in history"
              :key="entry.id"
            >
              <v-expansion-panel-header>{{ entry.created_at }} — {{ entry.actor_name }} — {{ entry.reason }}</v-expansion-panel-header>
              <v-expansion-panel-content>
                <p>Reference: {{ entry.id }}</p>
                <p>Account: {{ balanceLabel(entry.result.balance_before) }} → {{ balanceLabel(entry.result.balance_after) }}</p>
                <correction-changes :rows="entry.result.rows" />
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>
      </v-card-text>
      <v-card-actions>
        <v-btn
          text
          :disabled="busy || loading"
          @click="load"
        >
          Reload history
        </v-btn>
        <v-btn
          text
          :disabled="busy"
          @click="$emit('close')"
        >
          Close
        </v-btn>
        <v-spacer />
        <template v-if="tab === 0">
          <v-btn
            v-if="preview"
            text
            :disabled="busy"
            @click="preview = null"
          >
            Back to edit
          </v-btn>
          <v-btn
            v-if="!preview"
            color="primary"
            :loading="busy"
            :disabled="loading || !selected.length || reason.trim().length < 5"
            @click="getPreview"
          >
            Preview correction
          </v-btn>
          <v-btn
            v-else
            color="primary"
            :loading="busy"
            @click="save"
          >
            Save correction
          </v-btn>
        </template>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from 'axios'
import { mdiHelpCircleOutline } from '@mdi/js'
import CorrectionChanges from '@/components/partials/CorrectionChanges.vue'

function reference() {
  const bytes = new Uint8Array(16)
  window.crypto.getRandomValues(bytes)
  bytes[6] = (bytes[6] % 16) + 64
  bytes[8] = (bytes[8] % 64) + 128
  const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('')

  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

export default {
  components: { CorrectionChanges },
  props: {
    showDialog: Boolean,
    customerId: { type: String, default: '' },
    accountNumber: { type: String, default: '' },
    initialMonth: { type: String, default: '' },
  },
  data: () => ({
    rows: [],
    history: [],
    tab: 0,
    loading: false,
    busy: false,
    error: '',
    visibleMonthCount: 6,
    reason: '',
    sendSummary: false,
    preview: null,
    payload: null,
    icons: { mdiHelpCircleOutline },
    modes: [{ text: 'Actual reading', value: 'actual' }, { text: 'Estimated units', value: 'estimated' }],
  }),
  computed: {
    selected() { return this.rows.filter(row => row.selected) },
    sortedRows() { return [...this.rows].sort((a, b) => b.month.localeCompare(a.month)) },
    allMonths() { return [...new Set(this.sortedRows.map(row => row.month))] },
    displayRows() {
      const visibleMonths = this.allMonths.slice(0, this.visibleMonthCount)

      return this.sortedRows.filter(row => row.selected || visibleMonths.includes(row.month))
    },
    remainingMonthCount() {
      const visibleMonths = this.allMonths.slice(0, this.visibleMonthCount)
      const selectedMonths = this.selected.map(row => row.month)

      return this.allMonths.filter(month => !visibleMonths.includes(month) && !selectedMonths.includes(month)).length
    },
    hasSelectedOutsideWindow() {
      const visibleMonths = this.allMonths.slice(0, this.visibleMonthCount)

      return this.selected.some(row => !visibleMonths.includes(row.month))
    },
    endpoint() { return `users/${this.customerId}/reading-corrections` },
  },
  watch: {
    showDialog(value) { if (value) this.load() },
  },
  methods: {
    money(value) { return Number(value).toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) },
    balanceLabel(value) { return `KSh ${this.money(Math.abs(value))} ${value < 0 ? 'debt' : 'credit'}` },
    linkedPrevious(row) {
      const earlier = this.rows.slice(0, this.rows.indexOf(row)).filter(item => item.meter_id === row.meter_id)
      const prior = earlier[earlier.length - 1]

      return row.reading_baseline || !prior || (!row.selected && !(prior.selected && Number(prior.current_reading) !== prior.original_current)) ? row.previous_reading : prior.current_reading
    },
    loadMoreMonths() { this.visibleMonthCount += 6 },
    selectLatestMonths() {
      const latestMonths = this.allMonths.slice(0, 2)
      this.rows = this.rows.map(row => ({ ...row, selected: latestMonths.includes(row.month) }))
    },
    clearSelection() { this.rows = this.rows.map(row => ({ ...row, selected: false })) },
    tariffChanged(row) {
      if (!row.tariff_snapshot) return true

      return Number(row.unit_rate) !== Number(row.original_unit_rate)
        || Number(row.service_fee) !== Number(row.original_service_fee)
    },
    showError(error) {
      const data = error.response && error.response.data
      const validationMessages = data && data.errors ? [...new Set(Object.values(data.errors).flat())] : []
      this.error = validationMessages.length ? validationMessages.join(' ') : (data && data.message) || 'Unable to complete the request. Please try again.'
    },
    async load() {
      this.loading = true
      this.error = ''
      this.preview = null
      this.payload = null
      this.reason = ''
      this.sendSummary = false
      this.tab = 0
      this.rows = []
      this.history = []
      this.visibleMonthCount = 6
      try {
        const { data } = await axios.get(this.endpoint)
        this.history = data.history
        this.rows = data.readings
          .slice()
          .sort((a, b) => a.month.localeCompare(b.month))
          .map(row => ({
            ...row,
            original_current: Number(row.current_reading),
            selected: row.month === this.initialMonth,
            mode: row.adjusted_units === null ? 'actual' : 'estimated',
            unit_rate: row.tariff_snapshot
              ? row.tariff_snapshot.unit_rate
              : (row.suggested_unit_rate == null ? '' : row.suggested_unit_rate),
            original_unit_rate: row.tariff_snapshot ? row.tariff_snapshot.unit_rate : row.suggested_unit_rate,
            original_service_fee: row.service_fee,
          }))
        const defaultMonths = this.initialMonth && this.allMonths.includes(this.initialMonth)
          ? [this.initialMonth]
          : this.allMonths.slice(0, 2)
        this.rows = this.rows.map(row => ({ ...row, selected: defaultMonths.includes(row.month) }))
      } catch (error) { this.showError(error) } finally { this.loading = false }
    },
    async getPreview() {
      this.busy = true
      this.error = ''
      this.payload = {
        request_id: reference(),
        reason: this.reason.trim(),
        send_summary: this.sendSummary,
        edits: this.selected.map(row => ({
          id: row.id,
          mode: row.mode,
          current_reading: row.current_reading,
          previous_reading: row.previous_reading,
          reading_baseline: row.reading_baseline,
          adjusted_units: row.mode === 'estimated' ? row.adjusted_units : null,
          estimation_basis: row.estimation_basis,
          unit_rate: row.unit_rate === '' ? null : row.unit_rate,
          service_fee: row.service_fee,
          tariff_verified: this.tariffChanged(row),
        })),
      }
      try {
        const { data } = await axios.post(`${this.endpoint}/preview`, this.payload)
        this.preview = data
      } catch (error) { this.showError(error) } finally { this.busy = false }
    },
    async save() {
      this.busy = true
      this.error = ''
      try {
        const { data } = await axios.post(this.endpoint, { ...this.payload, preview_token: this.preview.preview_token })
        this.$notification.success(`Correction saved. Reference: ${data.reference}`)
        this.$emit('saved')
        this.$emit('close')
      } catch (error) {
        this.showError(error)
        if (error.response && error.response.status === 409) this.preview = null
      } finally { this.busy = false }
    },
  },
}
</script>

<style scoped>
.correction-grid td { min-width: 125px; padding-top: 12px !important; padding-bottom: 12px !important; }
.correction-grid td:first-child { min-width: 55px; }
.correction-grid td:nth-child(3) { min-width: 200px; }
.correction-grid td:nth-child(7) { min-width: 230px; }
.month-cell { white-space: nowrap; }
.historical-charge-field + .historical-charge-field { margin-top: 16px; }
</style>
